import { createSelector } from 'reselect';
import { selectors as concurrencySelectors } from '@saucelabs/sl-module-concurrency';
import { constants as userConstants } from '@saucelabs/sl-module-user';
import { selectors as tunnelSelectors } from '@saucelabs/sl-module-tunnels';
import { List, Set } from 'immutable';

import { selectors as testConfigurationSelectors } from '../../ducks/testConfiguration';
import commonUtils from '../sessionCommon/utils';
import {
  CONFIGURATION_TEST_TYPES,
} from '../../constants';
import constants from './constants';
import utils from './utils';
import groupUtils from './groupUtils';
import sortUtils from './sortUtils';

const {
  DEVICE_SORT_MODES,
  DEVICE_SUBSCRIPTION_VARS,
  MOBILE_SELECTION_FILTER,
  MOBILE_SELECTION_FILTER_CATEGORY,
  SORT_CATEGORIES,
} = constants;

const pinnedDeviceCompositeIdsLoadedSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('pinnedDeviceCompositeIdsLoaded')
);
const starDevicesCompositeIdsSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('starDevicesCompositeIds')
);
const unsortedDevicesSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('items')
);
const activeFilterSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('activeFilters')
);
const availableDeviceIdsSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('availableDeviceIds')
);
const sessionAvailableSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('sessionAvailable')
);
const sortModeSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('sortMode')
);

/**
 * A redux selector that returns a boolean which states if the current user is a free trial user
 * @param {Object} store - the redux store
 * @return {boolean} the boolean that represents if the user is currently a free trial user
 */
const isUserFreeTrialSelector = (store) => (
  store.user.getOrgUserType() === userConstants.USER_TYPE_FREE_TRIAL
);

/**
 * A redux selector that returns a boolean which states if the current user is an active free user
 * (either free trial or freemium)
 * @param {Object} store - the redux store
 * @return {boolean} the boolean that represents if the user is currently free (trial or freemium)
 */
const activeFreeUserSelector = ({ user }) => commonUtils.isActiveFreeUser(user);

/**
 * A redux selector that returns a boolean which states if the current user
 * is in an expired state (expired trial or expired subscription)
 * @param {Object} store - the redux store
 * @return {boolean} the boolean that represents if the user is currently an expired user
 */
const expiredUserSelector = (store) => {
  const userType = store.user.getOrgUserType();
  return userType === userConstants.USER_TYPE_FREE;
};

const filteredDevicesSelector = createSelector([
  (store) => store.apps.manual.realDeviceSelection.get('searchTerm'),
  activeFilterSelector,
  unsortedDevicesSelector,
  testConfigurationSelectors.mobileAppConfigurationSelector,
], (
  searchTerm,
  activeFilters,
  unsortedDevices,
  mobileAppConfiguration,
) => {
  const osFilters = activeFilters.get('os');
  const osVersionFilters = activeFilters.get('osVersion');
  const brandFilters = activeFilters.get('manufacturer'); // I'm a Map!
  const resolutionFilters = activeFilters.get('resolution');
  const formFactorFilters = activeFilters.get('formFactor');
  const showPrivateDevices = activeFilters.get('cloudType').has('PRIVATE');
  const showCarrierConnectivity = activeFilters.get('networkType').has('CARRIER');
  const compatibilityFilter = activeFilters.get(MOBILE_SELECTION_FILTER_CATEGORY.COMPATIBILITY);
  const filteredDevices = unsortedDevices.filter((device) => {
    if (!(
      new RegExp(searchTerm, 'i').test(device.name) ||
        new RegExp(searchTerm, 'i').test(device.descriptorId))
    ) {
      return false;
    }

    if (
      compatibilityFilter.size > 0 &&
      mobileAppConfiguration &&
      !utils.isAppStorageFileCompatibleWithRealDeviceDescriptor(
        mobileAppConfiguration,
        device,
      )
    ) {
      return false;
    }

    if (osFilters.size > 0 && !(osFilters.has(device.os))) {
      return false;
    }
    if (osVersionFilters.size > 0 && !(osVersionFilters.has(device.osVersion))) {
      return false;
    }
    if (
      brandFilters.size > 0 &&
      !device.manufacturers.some((brand) => brandFilters.includes(brand))
    ) {
      return false;
    }
    if (resolutionFilters.size > 0 && !resolutionFilters.has(`${device.resolutionWidth}x${device.resolutionHeight}`)) {
      return false;
    }
    if (formFactorFilters.size > 0 && !(formFactorFilters.has(device.formFactor))) {
      return false;
    }
    if (showPrivateDevices && !device.isPrivateDevice()) {
      return false;
    }
    if (showCarrierConnectivity && !device.connectivity.includes('CARRIER')) {
      return false;
    }
    return true;
  });
  return filteredDevices;
});

const sortedDevicesSelector = createSelector([
  activeFreeUserSelector,
  filteredDevicesSelector,
  sortModeSelector,
  starDevicesCompositeIdsSelector,
], (
  activeFreeUser,
  filteredDevices,
  sortMode,
  pinnedDeviceIds,
) => {
  const { FREE, OTHER, PHONES, PINNED, PRIVATE } = SORT_CATEGORIES;

  const groupedDevices = groupUtils.groupByCategory(
    filteredDevices.toArray(), pinnedDeviceIds.toArray(),
  );

  const freeDevices = activeFreeUser ? groupedDevices[FREE] : [];
  // default aka DEVICE_SORT_MODES.PREFERRED_FIRST
  let paidDevices = [
    ...groupedDevices[PINNED],
    ...groupedDevices[PRIVATE],
    ...groupedDevices[PHONES],
    ...groupedDevices[OTHER],
  ];

  if (sortMode === DEVICE_SORT_MODES.PINNED_FIRST) {
    const unpinnedDevices = [
      ...groupedDevices[PRIVATE],
      ...groupedDevices[PHONES],
      ...groupedDevices[OTHER],
    ];
    paidDevices = [
      ...groupedDevices[PINNED],
      ...unpinnedDevices.sort(sortUtils.sortByName),
    ];
  }

  if (sortMode === DEVICE_SORT_MODES.A_TO_Z) {
    paidDevices = paidDevices.sort(sortUtils.sortByName);
  }

  if (sortMode === DEVICE_SORT_MODES.Z_TO_A) {
    paidDevices = paidDevices.sort(sortUtils.sortByName).reverse();
  }

  return {
    [DEVICE_SUBSCRIPTION_VARS.FREE]: List(freeDevices),
    [DEVICE_SUBSCRIPTION_VARS.PAID]: List(paidDevices),
  };
});

const isManualMinutesExpiredSelector = createSelector([
  (store) => store.user,
  isUserFreeTrialSelector,
  (store) => store.usage?.usageUnits?.total_available_usage_units <= 0,
], (
  user,
  isOldFreeTrial,
  outOfUsageUnits,
) => (
  (isOldFreeTrial && user.get('manual_minutes') === 0) ||
    (user.isOnFreeTrialPlan() && outOfUsageUnits)
));

const mobileSelectionSelector = createSelector([
  availableDeviceIdsSelector,
  concurrencySelectors.concurrencySelector,
  (store) => store.apps.manual.realDeviceSelection.get('sessionAvailabilityLoaded'),
  sessionAvailableSelector,
  (store) => store.apps.manual.realDeviceSelection,
  starDevicesCompositeIdsSelector,
  (store) => store.user,
  tunnelSelectors.filteredUserTunnelsSelector,
  expiredUserSelector,
  sortedDevicesSelector,
  testConfigurationSelectors.realDeviceConfigurationSelector,
  testConfigurationSelectors.mobileAppConfigurationSelector,
  testConfigurationSelectors.testTypeSelector,
  testConfigurationSelectors.tunnelSelector,
  testConfigurationSelectors.isSessionLoadingSelector,
  activeFreeUserSelector,
  testConfigurationSelectors.urlSelector,
  isManualMinutesExpiredSelector,
],
(
  availableDeviceIds,
  concurrency,
  sessionAvailabilityLoaded,
  sessionAvailable,
  realDeviceSelection,
  starredRealDevices,
  user,
  userTunnels,
  expiredUser,
  sortedDevices,
  realDeviceConfiguration,
  mobileAppConfiguration,
  testType,
  tunnel,
  isSessionLoading,
  activeFreeUser,
  url,
  isManualMinutesExpired,
) => {
  const userSettings = user.get('organization').settings;
  const activeFilters = realDeviceSelection.get('activeFilters');

  return {
    activeFilters,
    activeFilterCarrier: activeFilters
      .get(MOBILE_SELECTION_FILTER_CATEGORY.NETWORK_TYPE)
      .has(MOBILE_SELECTION_FILTER.CARRIER),
    activeFilterCompatibility: activeFilters
      .get(MOBILE_SELECTION_FILTER_CATEGORY.COMPATIBILITY)
      .has(MOBILE_SELECTION_FILTER.COMPATIBILITY),
    activeFilterPhone: activeFilters
      .get(MOBILE_SELECTION_FILTER_CATEGORY.FORM_FACTOR)
      .has(MOBILE_SELECTION_FILTER.PHONE),
    activeFilterPrivate: activeFilters
      .get(MOBILE_SELECTION_FILTER_CATEGORY.CLOUD_TYPE)
      .has(MOBILE_SELECTION_FILTER.PRIVATE),
    activeFilterTablet: activeFilters
      .get(MOBILE_SELECTION_FILTER_CATEGORY.FORM_FACTOR)
      .has(MOBILE_SELECTION_FILTER.TABLET),
    allowedUsersRdcConcurrency: concurrency.user.get('allowed')?.rdc,
    availableDeviceIds,
    currentFacets: realDeviceSelection.get('currentFacets'),
    currentView: realDeviceSelection.get('currentView'),
    devices: sortedDevices,
    email: user.get('email'),
    expiredUser,
    isFreemiumCapable: user.isFreemiumCapable(),
    isFreemiumUser: user.isFreemiumUser(),
    isSessionLoading,
    isManualMinutesExpired,
    isOrgAdmin: user.isOrgAdmin(),
    // based on this thread: https://saucelabs.slack.com/archives/CA3ES45TR/p1633530438213600
    // rdc_enabled can be true with 0 devices
    isRdcEnabled: !!userSettings?.rdc_enabled && (realDeviceSelection.get('loading') || realDeviceSelection.get('items').size !== 0),
    isUnverifiedUser: !user.isVerified(),
    activeFreeUser,
    loading: realDeviceSelection.get('loading'),
    mobileAppConfiguration,
    realDeviceConfiguration,
    sauceConnectPublicRdcEnabled: userSettings?.can_use_tunnels_with_public_real_devices,
    searchTerm: realDeviceSelection.get('searchTerm'),
    sessionAvailabilityLoaded,
    sessionAvailable,
    sortMode: realDeviceSelection.get('sortMode'),
    starredRealDevices,
    testType,
    tunnel,
    tunnels: userTunnels,
    url,
  };
});

const vmdBannerSelector = createSelector([
  availableDeviceIdsSelector,
  concurrencySelectors.concurrencySelector,
  (store) => store.apps.manual.realDeviceSelection,
  sessionAvailableSelector,
  sortedDevicesSelector,
  testConfigurationSelectors.testTypeSelector,
  activeFreeUserSelector,
  (store) => store.user.get('organization').settings,
  (store) => store.user.getUserType(),
  (store) => store.usage?.usageUnits?.total_available_usage_units <= 0,
  (store) => store.usage?.nextFreemiumReplenishment,
], (
  availableDeviceIds,
  concurrency,
  realDeviceSelection,
  sessionAvailable,
  sortedDevices,
  testType,
  activeFreeUser,
  userSettings,
  userType,
  outOfUsageUnits,
  creditRefreshDate,
) => {
  const availableDeviceIdsLoaded = realDeviceSelection.get('availableDeviceIdsLoaded');
  const sessionAvailabilityLoaded = realDeviceSelection.get('sessionAvailabilityLoaded');

  const searchTerm = realDeviceSelection.get('searchTerm');

  const activeFilters = realDeviceSelection.get('activeFilters');
  const filterIsSet = activeFilters.some((filter) => filter.size);

  const availableDevicesCount = sortedDevices.PAID.count((device) => (
    !utils.isDeviceDisabled(
      device, availableDeviceIds, sessionAvailable,
    )
  )) + sortedDevices.FREE.count((device) => (
    !utils.isDeviceDisabled(
      device, availableDeviceIds, sessionAvailable,
    )
  ));
  const allDevicesBusy = availableDevicesCount === 0;

  const isRdcEnabled = !!userSettings?.rdc_enabled && (
    realDeviceSelection.get('loading') || realDeviceSelection.get('items').size !== 0);

  const isAndroidOnly = activeFilters.get(
    MOBILE_SELECTION_FILTER_CATEGORY.OS).equals(Set([MOBILE_SELECTION_FILTER.ANDROID]));
  const isVmdFriendlyConfiguration = (
    testType === CONFIGURATION_TEST_TYPES.WEB ||
      (testType === CONFIGURATION_TEST_TYPES.APP && isAndroidOnly)
  );

  return {
    hasVmdConcurrencyAvailable: concurrency.user.get('allowed')?.vms > concurrency.user.get('current')?.vms,
    shouldTrackEventForActiveFreeUser: (
      isRdcEnabled &&
      allDevicesBusy &&
      !!sortedDevices.FREE.size &&
      !searchTerm &&
      activeFreeUser &&
      availableDeviceIdsLoaded &&
      sessionAvailabilityLoaded &&
      isVmdFriendlyConfiguration &&
      !filterIsSet
    ),
    shouldTrackEventForDeviceSearch: (
      isRdcEnabled &&
      allDevicesBusy &&
      !!sortedDevices.PAID.size &&
      !!searchTerm &&
      availableDeviceIdsLoaded &&
      isVmdFriendlyConfiguration &&
      sessionAvailabilityLoaded
    ),
    shouldTrackEventForDeviceFilter: (
      isRdcEnabled &&
      allDevicesBusy &&
      !!sortedDevices.PAID.size &&
      filterIsSet &&
      availableDeviceIdsLoaded &&
      isVmdFriendlyConfiguration &&
      sessionAvailabilityLoaded
    ),
    isBannerVisible: (
      isRdcEnabled &&
      allDevicesBusy &&
      !!sortedDevices.PAID.size &&
      availableDeviceIdsLoaded &&
      sessionAvailabilityLoaded
    ),
    isVmdFriendlyConfiguration,
    testType,
    userType,
    outOfUsageUnits,
    creditRefreshDate,
  };
});

export default {
  availableDeviceIdsSelector,
  isManualMinutesExpiredSelector,
  mobileSelectionSelector,
  pinnedDeviceCompositeIdsLoadedSelector,
  sortModeSelector,
  starDevicesCompositeIdsSelector,
  vmdBannerSelector,
};

export {
  activeFreeUserSelector,
  filteredDevicesSelector,
  starDevicesCompositeIdsSelector,
};
